import type { RemoveShopifyIntegrationModalProps } from "@v1/types/modalTypes";

import * as React from "react";

import toast from "@v1/components/designSystem/alert/Toast";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Modal } from "@v1/components/designSystem/modal/Modal";
import { useModal } from "@v2/store/modalStore";
import { integrationRouter } from "@v2/trpc/routes/integration";
import { projectUtils } from "@v2/trpc/routes/project";
import { workspaceUtils } from "@v2/trpc/routes/workspace";

export const RemoveShopifyIntegrationModal: React.FC<
  RemoveShopifyIntegrationModalProps
> = ({ projectId, integrationId }) => {
  const { closeModal } = useModal();
  const { mutate: disconnectIntegration, isPending: isLoading } =
    integrationRouter.disconnectIntegrationFromProject.useMutation({
      onSuccess: () => {
        void workspaceUtils.getUserWorkspacesList.invalidate();
        void projectUtils.findByUserId.invalidate();
        void projectUtils.listWithStats.invalidate();
        void projectUtils.getV2.invalidate(projectId);
        toast({
          header: "Shopify Integration removed from Project",
        });
      },
      onSettled: () => {
        closeModal({ type: "removeShopifyIntegrationModal" });
      },
    });

  const handleConfirm = () => {
    disconnectIntegration({ projectId, integrationId });
  };

  return (
    <Modal
      title="Remove Shopify Integration"
      isOpen={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal({ type: "removeShopifyIntegrationModal" });
        }
      }}
      size="base"
      footer={
        <div className="flex flex-row gap-2">
          <DeprecatedButton
            variant="secondary"
            size="base"
            onClick={() => {
              return closeModal({ type: "removeShopifyIntegrationModal" });
            }}
          >
            Cancel
          </DeprecatedButton>
          <DeprecatedButton
            variant="danger"
            size="base"
            type="submit"
            isLoading={isLoading}
            onClick={handleConfirm}
          >
            Confirm
          </DeprecatedButton>
        </div>
      }
    >
      <p className="text-default text-sm">
        Removing the Shopify integration from this project will disconnect all
        dynamic data, and you won't be able to publish new changes until you
        install the Shopify integration again.
      </p>
    </Modal>
  );
};
